import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, Checkbox, FormControlLabel, FormInputText, InputLabel, Select } from '@hopskipdrive/react-components';
import * as Yup from 'yup';

import { useRiderQuery } from '../../../hooks/useRiderQuery';
import { FlexContent } from '../styles';
import { newISharedBillingFormType } from '../types';
import { useOrganizersHook } from '../useOrganizers';

export const SharedBillingFormV2 = ({
  riderId,
  useAccounts,
  useOrganizers,
}: {
  riderId?: string;
  useAccounts: () => Nullable<Account[]>;
  useOrganizers: () => useOrganizersHook;
}) => {
  const {
    watch,
    control,
    clearErrors,
    formState: { errors },
    setValue,
  } = useFormContext<newISharedBillingFormType>();
  const isSharedBillingRider = watch('isSharedBillingRider');
  const accountId = watch('sharedBillingRider.residenceAccountId');
  const [accountChanged, setAccountChanged] = useState(false);
  const accounts = useAccounts();
  const { organizers, fetchOrganizers, clearOrganizers, isLoading } = useOrganizers();
  const { riderDetails, fetchRiderBySlug } = useRiderQuery();

  useEffect(() => {
    riderId && fetchRiderBySlug(riderId);
  }, [riderId, fetchRiderBySlug]);

  useEffect(() => {
    if (accountId) {
      fetchOrganizers(Number(accountId));
    }

    // Check if the account has changed
    if (riderDetails?.sharedBillingRider?.residenceAccountId !== accountId) {
      setAccountChanged(true);
    }
  }, [accountId]);

  return (
    <Box display="flex" flexDirection="column" gap="1rem" alignSelf="stretch">
      <Box display="flex" flexWrap="wrap" flex="1" gap="2rem">
        <FlexContent>
          <FormControlLabel
            label={'This rider’s residence address is in a different district'}
            labelPlacement="end"
            control={
              <Controller
                control={control}
                name="isSharedBillingRider"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    color="primary"
                    checked={value}
                    disabled={!accounts || accounts.length === 0}
                    sx={{ marginLeft: '0.5rem' }}
                    onChange={(e) => {
                      onChange(e.target.checked);
                      if (!e.target.checked) {
                        clearErrors(['sharedBillingRider']);
                      }
                    }}
                  />
                )}
              />
            }
          />
        </FlexContent>
        <FlexContent>
          <InputLabel htmlFor="residenceAccountId">Residence District</InputLabel>
          <Controller
            control={control}
            name="sharedBillingRider.residenceAccountId"
            render={({ field: { onChange, value } }) => (
              <Select
                id="residenceAccountId"
                options={accounts?.map((a) => ({ label: a.name, value: a.id })) || []}
                value={(accounts?.find((a) => a.id === value) && value) || undefined}
                setValue={(value) => {
                  onChange(value);
                  if (!value) {
                    clearOrganizers();
                  }
                  setValue('sharedBillingRider.residenceAccountContactId', null);
                  setValue('sharedBillingRider.residenceAccountContactEmail', '');
                }}
                isLoading={!!value && !accounts}
                renderInput={(params) => (
                  <FormInputText
                    {...params}
                    type="text"
                    disabled={(value && (!accounts || accounts.length === 0)) || !isSharedBillingRider}
                    id="residenceAccountId"
                    inputProps={{ ...params.inputProps, 'aria-required': !!isSharedBillingRider }}
                    variant="outlined"
                    data-testid="residenceAccountId"
                    placeholder="School District"
                    error={!!errors.sharedBillingRider?.residenceAccountId}
                    helperText={errors.sharedBillingRider?.residenceAccountId?.message}
                  />
                )}
              />
            )}
          />
        </FlexContent>
      </Box>
      <Box display="flex" flexWrap="wrap" flex="1" gap="2rem">
        <FlexContent>
          <InputLabel htmlFor="residenceAccountContactId">Contact Name for notifications</InputLabel>
          <Controller
            name="sharedBillingRider.residenceAccountContactId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                id="residenceAccountContactId"
                options={organizers?.map((o) => ({ label: o.name, value: o.id })) || []}
                value={(organizers?.find((o) => o.id === value) && value) || undefined}
                setValue={(v) => {
                  // If contactName changes, find the corresponding organizer and set the email
                  const organizerEmail = organizers?.find((o) => o.id === Number(v))?.email;

                  onChange(v);
                  setValue('sharedBillingRider.residenceAccountContactEmail', organizerEmail ?? '');
                }}
                isLoading={isLoading}
                renderInput={(params) => (
                  <FormInputText
                    {...params}
                    type="text"
                    disabled={
                      (value && (!organizers || organizers.length === 0)) ||
                      !isSharedBillingRider ||
                      (!!(riderDetails?.sharedBillingRider?.status === 'approved') && !accountChanged) // Disable Contact Name dropdown if SBR is already approved and account is not changed
                    }
                    id="residenceAccountContactId"
                    inputProps={{ ...params.inputProps, 'aria-required': !!isSharedBillingRider }}
                    variant="outlined"
                    data-testid="residenceAccountContactId"
                    placeholder="Name"
                    error={!!errors.sharedBillingRider?.residenceAccountContactId}
                    helperText={errors.sharedBillingRider?.residenceAccountContactId?.message}
                  />
                )}
              />
            )}
          />
        </FlexContent>
        <FlexContent>
          <InputLabel htmlFor="residenceAccountContactEmail">Contact Email</InputLabel>
          <Controller
            name="sharedBillingRider.residenceAccountContactEmail"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormInputText
                id="residenceAccountContactEmail"
                value={value}
                name="sharedBillingRider.residenceAccountContactEmail"
                variant="outlined"
                data-testid="residenceAccountContactEmail"
                fullWidth
                placeholder="Email"
                type="email"
                disabled={true}
                onChange={onChange}
              />
            )}
          />
        </FlexContent>
      </Box>
    </Box>
  );
};

export function sharedBillingValidationSchemaV2() {
  return Yup.object().shape({
    sharedBillingRider: Yup.object().when('isSharedBillingRider', {
      is: true,
      then: Yup.object().shape({
        residenceAccountContactId: Yup.number().nullable().required('Required Field'),
        residenceAccountId: Yup.number().nullable().required('Required Field'),
      }),
    }),
  });
}
